import { useState, useEffect } from 'react';
import {
  Card, Button, Upload, Table, Tag, message, Space, Typography, Popconfirm, Row, Col, Statistic,
} from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../../api';

const { Text } = Typography;

export default function DeclarationAdmin() {
  const [pdfs, setPdfs] = useState([]);
  const [health, setHealth] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const loadData = async () => {
    try {
      const [h, p] = await Promise.all([
        api.get('/declaration/health'),
        api.get('/declaration/pdfs'),
      ]);
      setHealth(h.data);
      setPdfs(p.data.items || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleExcelUpload = async (file) => {
    setExcelLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/declaration/upload-excel', fd);
      message.success(res.data.message || 'Excel 已更新');
      loadData();
    } catch (e) {
      message.error(e.response?.data?.detail || '上传失败');
    } finally {
      setExcelLoading(false);
    }
    return false;
  };

  const handlePdfUpload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/declaration/upload-pdf', fd);
      message.success(res.data.message || 'PDF 已上传');
      loadData();
    } catch (e) {
      message.error(e.response?.data?.detail || '上传失败');
    }
    return false;
  };

  const handleDelete = async (filename) => {
    try {
      await api.delete(`/declaration/pdfs/${encodeURIComponent(filename)}`);
      message.success('已删除');
      loadData();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: '文件名', dataIndex: 'filename' },
    { title: '类型', dataIndex: 'document_type', width: 120, render: (v) => <Tag>{v}</Tag> },
    { title: '大小', dataIndex: 'size', width: 110, render: (v) => `${(v / 1024).toFixed(1)} KB` },
    {
      title: '操作',
      width: 90,
      render: (_, r) => (
        <Popconfirm title="确认删除该 PDF？" onConfirm={() => handleDelete(r.filename)}>
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card title="资料维护（批号 + PDF）">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card><Statistic title="批号资料数" value={health?.batches || 0} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="PDF 文件数" value={health?.pdfs || 0} /></Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="DeepSeek 智能问答"
              value={health?.ai_configured ? '已配置' : '未配置'}
              valueStyle={{ color: health?.ai_configured ? '#52c41a' : '#999' }}
            />
          </Card>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Card size="small" title="1. 上传库存批号 Excel（全量更新批号/合同号）">
          <Upload beforeUpload={handleExcelUpload} showUploadList={false} accept=".xlsx,.xlsm">
            <Button type="primary" icon={<UploadOutlined />} loading={excelLoading}>
              上传 Excel
            </Button>
          </Upload>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Excel 需包含「批号」「合同号」列，可选「规格型号」「备注」列。上传后全量替换现有批号资料。
            </Text>
          </div>
        </Card>

        <Card size="small" title="2. 上传 PDF 资料（文件名需包含合同号）">
          <Upload beforeUpload={handlePdfUpload} showUploadList={false} accept=".pdf" multiple>
            <Button icon={<UploadOutlined />}>上传 PDF（可多选）</Button>
          </Upload>
        </Card>

        <Card size="small" title={`3. 已上传 PDF 列表（${pdfs.length}）`}>
          <Table rowKey="filename" columns={columns} dataSource={pdfs} size="small" pagination={false} />
        </Card>
      </Space>
    </Card>
  );
}
